import { FC, useEffect, useState } from "react";
import { CardHeader, Drawer, useMediaQuery, Card, CardContent, Box, Button, FormHelperText, 
  Grid, TextField, Stack, Typography, Tooltip} from "@mui/material";
import Create from "@/controller/Create";
import WithSave from "@/controller/WithSave";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useListContext } from "ra-core";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { addDays } from "date-fns";
import { CreateRegisterCodeInput, RegisterCodeResponse } from "@/gen/api/user";
import ReadonlyField from "@/components/input/ReadonlyField";
import { autoCopyTextFieldProps } from "@/utils/clipboard";




const CreateRegisterCode: FC<{
  resource: string,
  fixedAllowUsage?: number,
  open: boolean,
  setOpen: (x:boolean)=>void,
}> = ({ resource, open, setOpen, fixedAllowUsage, }) => {
  const { refetch } = useListContext()
  const handleClose = (): void => {
    setOpen(false);
  };

  const format = useUserTimeFormat()
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const [states, setStates] = useState({
    tick: 0,
    validDays: 180,
    allowedUsage: fixedAllowUsage ?? 0,
    error: null as string|null,
    codeDto: null as RegisterCodeResponse|null,
    copied: false,
    formErrs: { validDays: false, allowedUsage: false },
  })
  useEffect(() => {
    const timer = setInterval(() => {
      setStates(v => ({...v, tick: v.tick + 1}))
    }, 60000)
    return () => clearInterval(timer)
  }, [])

  const savedHandlers = useDefaultSavedHandlers("Create Registration Code")

  return (
    <Drawer
      anchor="right"
      // onOpen={() => {}}
      onClose={handleClose}
      open={open}
      PaperProps={{ sx: { p: 2, width: width, } }}
    >
      <Card sx={{ overflowY: 'auto', mb: 4, }} >
        <CardHeader title="Create Registration Code" />
        <CardContent>
          <Grid container spacing={4} >
            <Grid item xs={12} >
              <Stack direction="row" alignItems="baseline">
                <Typography variant="subtitle2" sx={{ mb: 5 }}>
                  Code would be valid for 
                </Typography>
                <TextField type="number" size="small" 
                  sx={{ width: 140, mx: 1,
                    '& p.MuiFormHelperText-root': { width: 220, ml: 0, color: theme => theme.palette.mode === 'dark' ? '#94a1ae' : undefined }
                  }} 
                  value={states.validDays ?? 1} 
                  onChange={(e) => setStates(v => {
                    if (!e.target.value) {
                      return ({...v, validDays: '' as any, formErrs: {...v.formErrs, validDays: true }})
                    }
                    const v1 = ({...v, validDays: +e.target.value})
                    v1.formErrs.validDays = false
                    if (Number.isNaN(v1.validDays)) { v1.validDays = 0 }
                    if (!Number.isInteger(v1.validDays)) { v1.validDays = Math.round(v1.validDays) }
                    if (v1.validDays < 1) { v1.validDays = 1 }
                    else if (v1.validDays > 180) { v1.validDays = 180 }
                    return v1
                  })} 
                  error={states.formErrs.validDays}
                  helperText={
                    states.formErrs.validDays ? 'This field is required' :
                    `Until ${format(addDays(new Date(), states.validDays), 'Pp')}`}
                />
                <Typography variant="subtitle2" >
                  day(s).
                </Typography>
              </Stack>
            </Grid>
            {fixedAllowUsage == null &&
              <Grid item xs={12} >
                <Stack direction="row" alignItems="baseline">
                  <Typography variant="subtitle2" sx={{ mb: 5 }}>
                    Maximum Allowed Usage:
                  </Typography>
                  <TextField type="number" size="small" sx={{ width: 140, ml: 1 }} 
                    disabled={Boolean(states.codeDto)}
                    value={states.allowedUsage}
                    onChange={(e) => setStates(v => {
                      if (!e.target.value) {
                        return ({...v, allowedUsage: '' as any, formErrs: {...v.formErrs, allowedUsage: true }})
                      }
                      const v1 = ({...v, allowedUsage: +e.target.value})
                      v1.formErrs.allowedUsage = false
                      if (Number.isNaN(v1.allowedUsage)) { v1.allowedUsage = 0 }
                      if (!Number.isInteger(v1.allowedUsage)) { v1.allowedUsage = Math.round(v1.allowedUsage) }
                      if (v1.allowedUsage < 1) { v1.allowedUsage = 0 }
                      return v1
                    })}
                    error={states.formErrs.allowedUsage}
                    helperText={
                      states.formErrs.allowedUsage ? 'This field is required' :
                      states.allowedUsage < 1 ? "Effectively unlimited" : null}
                  />
                </Stack>
              </Grid>}
          </Grid>
          {states.error && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {states.error}
              </FormHelperText>
            </Box>
          )}
          {states.codeDto &&
            <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
              <Box sx={{my: 2}}>
                <ReadonlyField 
                  id="code-copy"
                  label="Generated Registration Code"
                  value={states.codeDto!.code!}
                  {...autoCopyTextFieldProps(
                    () => setStates(v => ({...v, copied: true})), 
                    () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                  )}
                  {...{inputProps: {tabIndex: 0}}}
                />
                <FormHelperText>
                  Please copy this registration code and use it for your registration process. By default, the registration code will disappear after it has been used.
                </FormHelperText>
              </Box>
            </Tooltip>}
          <Stack justifyContent="space-between" direction="row" sx={{ my: 2 }} >
            {states.codeDto 
              ? <Button color="primary" variant="outlined" onClick={handleClose} sx={{ alignSelf: 'flex-start'}}>
                  Close
                </Button>
              : <Create resource={resource} basePath="" 
                  onFailure={savedHandlers.onFailure} 
                  onSuccess={savedHandlers.onSuccess.merge(refetch)}
                >
                  <WithSave 
                    render={({ save, saving }) => (
                      <>
                        <Button
                          color="primary"
                          disabled={saving}
                          variant="outlined"
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          disabled={saving || states.formErrs.allowedUsage || states.formErrs.validDays}
                          type="submit"
                          variant="contained"
                          onClick={() => {
                            setStates(v => ({...v, error: null}))
                            const payload : CreateRegisterCodeInput = {
                              validInHours: states.validDays * 24,
                              maxAllowedUsage: states.allowedUsage < 1 ? null : states.allowedUsage,
                            }
                            save(payload, {
                              onSuccess: (resp: {data:any}) => {
                                setStates(v => ({...v, codeDto: resp.data}))
                              },
                              onFailure: (err:any) => { 
                                setStates(v => ({...v, error: err.message}))
                              }
                            })
                          }}
                        >
                          Create
                        </Button>
                      </>
                    )}
                  />
                </Create>}
          </Stack>
        </CardContent>
      </Card>
    </Drawer>
  )
}

export default CreateRegisterCode;
